import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ActionBar } from "../components/ActionBar";
import { IconButton } from "../components/Button";
import { TrendChart, type Point } from "../components/Chart";
import { ConfirmSheet } from "../components/ConfirmSheet";
import { Hero } from "../components/Hero";
import { Insight } from "../components/Insight";
import { LogWeightSheet } from "../components/LogWeightSheet";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import { bmiBandsLine, bodyFatRange, fatFreeMass, healthyWeightRange, healthyWeightRangeAsian, whtrAimCm } from "../domain/body";
import { cycleAffected, cycleOn } from "../domain/cycle";
import { waterSwing } from "../domain/cycleView";
import { addDays, dayLabel, dayMonth, todayStr } from "../domain/date";
import { bmi } from "../domain/energy";
import type { Insight as InsightData } from "../domain/insight";
import { bodyInsights, energyView, readingsTagged, signed, trendLabel, trendLine, trendUnit, trendView } from "../domain/progressView";
import { weightSeries, type Pt } from "../domain/trend";
import type { Measure } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";

/** Entries shown before "N earlier entries"; a year of weekly weigh-ins is a long list. */
const FIRST_ENTRIES = 5;

type Span = 28 | 90 | 0;
const SPANS: { value: Span; label: string }[] = [
  { value: 28, label: "28 days" },
  { value: 90, label: "90 days" },
  { value: 0, label: "All" },
];

function entryText(m: Measure): string {
  return [m.w ? `${m.w.toFixed(1)} kg` : null, m.waist ? `${m.waist.toFixed(1)} cm` : null].filter(Boolean).join(" · ");
}

/** The chart's window: the last `span` days, or everything; never fewer than two points. */
function windowed(ps: Pt[], today: string, span: Span): Point[] {
  const from = span ? addDays(today, -(span - 1)) : "";
  const kept = span ? ps.filter((p) => p.date >= from) : ps;
  return (kept.length >= 2 ? kept : ps).map((p) => [p.date, p.v]);
}

function pick(list: InsightData[], ...keys: string[]): InsightData[] {
  return keys.map((k) => list.find((i) => i.key === k)).filter((i): i is InsightData => !!i);
}

/**
 * /progress/body — the body in full (ADR-033): the weight reading and its trend (cycle-affected
 * readings stepped over), the waist and its 0.50 line, the figures height, age and sex turn the
 * two into, how the scale behaves, and every entry with its delete.
 */
export default function ProgressBody() {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const [logOpen, setLogOpen] = useState(false);
  const [all, setAll] = useState(false);
  const [span, setSpan] = useState<Span>(0);
  const [doomed, setDoomed] = useState<Measure | null>(null);

  const today = todayStr();
  const cyc = cycleOn(S);
  const affected = (ds: string) => cycleAffected(S, ds);
  const tv = trendView(S, "weight", today, cyc ? affected : undefined);
  const wv = trendView(S, "waist", today);
  const ins = bodyInsights(S, today, affected);
  // Each logged start's window — 5 days before through day 3 — shaded, and the readings in it
  // drawn in cycle ink: they run high on water, so they are shown and named, never dropped.
  const cycBands = cyc ? (S.cycle.starts || []).map((st) => ({ from: addDays(st, -5), to: addDays(st, 2) })) : undefined;
  const weightTagged = cyc ? new Set(readingsTagged(S, affected, today).readings.map((r) => r.date)) : undefined;
  const waistTagged = cyc ? new Set(wv.raw.filter((p) => affected(p.date)).map((p) => p.date)) : undefined;

  /* ---------- your numbers ---------- */
  const weight = tv.now;
  const height = S.profile.heightCm;
  const bmiNow = weight !== null && height ? bmi(S, weight) : null;
  const bmiStart = S.profile.startWeight && height ? bmi(S, S.profile.startWeight) : null;
  const fat = weight !== null ? bodyFatRange(S, weight, today) : null;
  const ffm = weight !== null && fat ? fatFreeMass(weight, fat.mid) : null;
  const healthy = height ? healthyWeightRange(S) : null;
  const asian = height ? healthyWeightRangeAsian(S) : null;
  const aim = whtrAimCm(S);

  /* ---------- entries ---------- */
  const ms = S.measures.slice().sort((a, b) => (a.date < b.date ? 1 : -1));
  const shown = all ? ms : ms.slice(0, FIRST_ENTRIES);
  const earlier = ms.length - shown.length;

  /* ---------- connected ---------- */
  const ev = energyView(S, today, 28, affected);
  const weights = new Map(weightSeries(S).map((p) => [p.date, p.v]));
  const swing = cyc ? waterSwing(S, today, (ds) => weights.get(ds) ?? null) : null;

  const del = () => {
    if (!doomed) return;
    const date = doomed.date;
    update((d) => {
      d.measures = d.measures.filter((x) => x.date !== date);
    });
    setDoomed(null);
    showToast("Entry deleted");
  };

  const waistFirst = wv.raw[0];
  const waistLast = wv.raw[wv.raw.length - 1];

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Body" back={{ to: "/progress", label: "Back to Progress" }} />
          <div className="pg">
            <Hero
              label={trendLabel(tv, today)}
              figure={tv.now === null ? null : tv.now.toFixed(1)}
              unit={trendUnit(tv)}
              line={trendLine(tv)}
              sub={tv.raw.length ? undefined : "Log one and its trend starts here."}
              visual={
                tv.raw.length >= 2 ? (
                  <div className="pg-herorows">
                    <TrendChart
                      pts={windowed(tv.raw, today, span)}
                      trend={windowed(tv.trend, today, span)}
                      target={tv.target ?? undefined}
                      unit={tv.unit}
                      readings="weigh-ins"
                      bands={cycBands}
                      tagged={weightTagged}
                    />
                    <Segmented options={SPANS} value={span} onChange={setSpan} ariaLabel="Chart window" />
                  </div>
                ) : undefined
              }
            />

            <RowGroup heading="Waist">
              {waistLast ? (
                <>
                  <div className={`k-chapter__leads${waistFirst && wv.raw.length > 1 ? " k-chapter__leads--split" : ""}`}>
                    <div className="k-chapter__lead">
                      <span className="k-chapter__leadfig">
                        {waistLast.v.toFixed(1)}
                        <span className="k-chapter__leadunit">cm</span>
                      </span>
                      <span className="k-chapter__leadlabel">Waist · {dayLabel(waistLast.date)}</span>
                      {wv.rate !== null ? (
                        <span className="k-chapter__leadnote">{signed(wv.rate, 1)} cm/week</span>
                      ) : null}
                    </div>
                    {wv.raw.length > 1 ? (
                      <div className="k-chapter__lead">
                        <span className="k-chapter__leadfig">
                          {signed(waistLast.v - waistFirst.v, 1)}
                          <span className="k-chapter__leadunit">cm</span>
                        </span>
                        <span className="k-chapter__leadlabel">Since {dayMonth(waistFirst.date)}</span>
                        <span className="k-chapter__leadnote">from {waistFirst.v.toFixed(1)} cm</span>
                      </div>
                    ) : null}
                  </div>
                  {wv.raw.length >= 2 ? (
                    <div className="pg-block">
                      <TrendChart
                        pts={windowed(wv.raw, today, span)}
                        trend={windowed(wv.trend, today, span)}
                        target={aim ?? undefined}
                        unit="cm"
                        color="var(--brand)"
                        readings="measurements"
                        bands={cycBands}
                        tagged={waistTagged}
                      />
                    </div>
                  ) : null}
                  {pick(ins, "body.composition", "body.waist").map((i) => (
                    <Insight key={i.key} insight={i} />
                  ))}
                </>
              ) : (
                pick(ins, "body.waist").map((i) => <Insight key={i.key} insight={i} first />)
              )}
            </RowGroup>

            <RowGroup heading="Your numbers">
              <NavRow
                kind="value"
                title="BMI"
                subtitle={
                  bmiNow === null
                    ? !height
                      ? "needs your height in Body & goals"
                      : "after your first weigh-in"
                    : bmiStart !== null && Math.abs(bmiStart - bmiNow) >= 0.05
                      ? `${bmiBandsLine(bmiNow)} · from ${bmiStart.toFixed(1)}${
                          S.profile.startDate ? ` on ${dayMonth(S.profile.startDate.slice(0, 10))}` : ""
                        }`
                      : bmiBandsLine(bmiNow)
                }
                value={bmiNow === null ? "–" : bmiNow.toFixed(1)}
              />
              <NavRow
                kind="value"
                title="Body fat"
                subtitle={
                  fat
                    ? `${Math.round(fat.low)}–${Math.round(fat.high)} % from BMI, age and sex${
                        ffm !== null ? ` · fat-free mass ~${Math.round(ffm)} kg est.` : ""
                      }`
                    : weight === null
                      ? "after your first weigh-in"
                      : "needs your year of birth and sex in Body & goals"
                }
                value={
                  fat ? (
                    <>
                      ~{Math.round(fat.mid)} % <Tag tone="est" />
                    </>
                  ) : (
                    "–"
                  )
                }
              />
              <NavRow
                kind="value"
                title={height ? `Healthy range for ${height} cm` : "Healthy range"}
                subtitle={asian ? `${Math.round(asian.low)}–${Math.round(asian.high)} kg on Asian bands` : "needs your height"}
                value={healthy ? `${Math.round(healthy.low)}–${Math.round(healthy.high)} kg` : "–"}
              />
              {pick(ins, "body.whtr").map((i) => (
                <Insight key={i.key} insight={i} />
              ))}
            </RowGroup>

            <RowGroup heading="Weigh-in rhythm">
              {pick(ins, "body.rhythm", "body.cycle").map((i, n) => (
                <Insight key={i.key} insight={i} first={n === 0} />
              ))}
            </RowGroup>

            <RowGroup heading={`All entries · ${ms.length}`}>
              {ms.length ? (
                <>
                  {shown.map((m) => (
                    <NavRow
                      key={m.date}
                      kind="action"
                      title={dayLabel(m.date)}
                      subtitle={
                        cyc && m.w && affected(m.date) ? (
                          <>
                            {entryText(m)} <Tag tone="cycle" />
                          </>
                        ) : (
                          entryText(m)
                        )
                      }
                      action={
                        <IconButton
                          icon={I.trash}
                          label={`Delete the entry for ${dayLabel(m.date)}`}
                          onClick={() => setDoomed(m)}
                        />
                      }
                    />
                  ))}
                  {earlier > 0 ? (
                    <NavRow
                      title={`${earlier} earlier ${earlier === 1 ? "entry" : "entries"}`}
                      subtitle={`from ${dayMonth(ms[ms.length - 1].date)}`}
                      onClick={() => setAll(true)}
                    />
                  ) : null}
                </>
              ) : (
                <NavRow kind="value" title="No entries yet" subtitle="Log a weigh-in and it lands here." />
              )}
            </RowGroup>
            {ms.length ? <p className="pg-note">Deleting an entry asks first. It also changes the trend.</p> : null}

            <RowGroup heading="Connected">
              <NavRow
                title="Energy"
                icon={I.flame}
                subtitle={ev.deficit !== null ? `What drives it · ${signed(Math.round(ev.deficit / 10) * 10)} kcal a day est.` : "What drives it"}
                to="/progress/energy"
              />
              {cyc ? (
                <NavRow
                  title="Cycle"
                  icon={I.drop}
                  subtitle={
                    swing && swing.kg >= 0.05
                      ? `Water swing · ${signed(swing.kg, 1)} kg before a period`
                      : "Water swing · after two cycles of weigh-ins"
                  }
                  to="/progress/cycle"
                />
              ) : null}
            </RowGroup>
          </div>

          <ActionBar
            primary={{
              label: "Log weight",
              icon: I.plus,
              onClick: () => setLogOpen(true),
            }}
          />
        </Screen>

        <LogWeightSheet open={logOpen} onClose={() => setLogOpen(false)} />
        {doomed ? (
          <ConfirmSheet
            title={`Delete the ${dayLabel(doomed.date)} entry?`}
            sub={entryText(doomed)}
            confirm={{ label: "Delete entry", onClick: del }}
            cancel="Keep it"
            onClose={() => setDoomed(null)}
          >
            <p className="pg-note">The trend is worked out again without it.</p>
          </ConfirmSheet>
        ) : null}
      </IonContent>
    </IonPage>
  );
}
